import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, UserRound, Send, Loader2, History, BadgeCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { VersionCompare } from "@/components/VersionCompare";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClientSelect } from "@/components/ClientSelect";
import { api, errMsg } from "@/lib/api";

export const useStaff = () => {
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  return staff;
};

export const StudyMeta = ({ study, clients, onUpdated }) => {
  const { user } = useAuth();
  const staff = useStaff();
  const [history, setHistory] = useState(false);
  const assignee = staff.find((s) => s.id === study.assigned_to);
  const [preparedBy, setPreparedBy] = useState(study.prepared_by || "");
  const [sending, setSending] = useState(false);
  const emailStudy = async () => {
    setSending(true);
    try { const r = await api.post(`/studies/${study.id}/email`); toast.success(`Study emailed to ${r.data.sent_to}`); }
    catch (e) { toast.error(errMsg(e, "Could not email study")); } finally { setSending(false); }
  };
  useEffect(() => setPreparedBy(study.prepared_by || ""), [study.prepared_by]);
  const save = async (patch) => {
    try { const r = await api.put(`/studies/${study.id}`, patch); onUpdated(r.data); toast.success("Study details saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="no-print rounded-xl border border-slate-800 bg-slate-900/70 p-5 grid md:grid-cols-12 gap-4 items-end" data-testid="study-meta">
      <div className="md:col-span-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><UserRound className="h-3.5 w-3.5" />Client (PDF cover)</Label>
        <ClientSelect value={study.client_id} onChange={(v) => save({ client_id: v })} clients={clients} testId="study-client-select" />
      </div>
      <div className="md:col-span-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-400">Prepared by</Label>
        <Input data-testid="study-prepared-by" value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)}
          onBlur={() => preparedBy !== (study.prepared_by || "") && save({ prepared_by: preparedBy })} className="bg-slate-950/60 border-slate-700" />
      </div>
      <div className="md:col-span-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-1"><BadgeCheck className="h-3.5 w-3.5" />Assigned to (gets alerts)</Label>
        {user?.role === "admin" ? (
          <Select value={study.assigned_to || "none"} onValueChange={(v) => save({ assigned_to: v === "none" ? "" : v })}>
            <SelectTrigger data-testid="study-assign-select" className="bg-slate-950/60 border-slate-700"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="none">Unassigned</SelectItem>
              {staff.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`assign-option-${s.id}`}>{s.name} ({s.email})</SelectItem>)}
            </SelectContent>
          </Select>
        ) : <p data-testid="study-assignee" className="h-10 flex items-center text-sm text-slate-300">{assignee ? assignee.name : "Unassigned"}</p>}
      </div>
      <div className="md:col-span-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-400">Delivery due date</Label>
        <DatePicker testId="study-due-date" value={study.due_date || ""} onChange={(v) => save({ due_date: v })} placeholder="No due date" />
      </div>
      <div className="md:col-span-4 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider text-slate-400">Loan deadline</Label>
        <DatePicker testId="study-loan-deadline" value={study.loan_deadline || ""} onChange={(v) => save({ loan_deadline: v })} placeholder="No loan deadline" />
      </div>
      <Button asChild variant="outline" data-testid="study-create-invoice" className="md:col-span-4 rounded-full border-slate-700 bg-transparent">
        <Link to={`/invoices?study=${study.id}`}><Receipt className="h-4 w-4 mr-2" />Invoice</Link>
      </Button>
      <Button data-testid="study-email-client" disabled={!study.client_id || sending} onClick={emailStudy} className="md:col-span-4 rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400">
        {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Email to client
      </Button>
      <Button variant="outline" data-testid="study-history-btn" onClick={() => setHistory(true)} className="md:col-span-4 rounded-full border-slate-700 bg-transparent"><History className="h-4 w-4 mr-2" />Version history</Button>
      <VersionCompare open={history} onOpenChange={setHistory} studyId={study.id} current={study}
        onSnapshot={() => api.post(`/studies/${study.id}/versions`).then(() => toast.success("Snapshot saved")).catch((e) => toast.error(errMsg(e)))}
        onRestore={(vid) => api.post(`/studies/${study.id}/versions/${vid}/restore`).then((r) => { onUpdated(r.data); toast.success("Version restored — previous version saved in history"); }).catch((e) => { toast.error(errMsg(e, "Restore failed")); throw e; })} />
    </div>
  );
};
